import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useAsync, useAsyncFn, useToggle } from 'react-use';
import { AnimatePresence, motion } from 'framer-motion';
import cn from 'classnames';

import { __t, MessengersBlockKind } from '@mssgme/shared';
import { NotFoundError } from '@mssgme/api-client';
import { useBooleanHandler, useNavigationUtils } from '@mssgme/hooks';
import {
    handlesPaymentsStatus,
    useCRMData,
    useFrontAPI,
    useModalThemes,
    usePageTheme,
    usePaymentConfigs,
    useRenderer,
} from '../../hooks';
import { ThemedButton, ThemedModal } from '../../UI';
import { PAYMENT_PROVIDER_RENDERERS } from '../OrderPage';

import styles from './UserOrderPage.scss';

UserOrderPage.propTypes = {
    orderId: PropTypes.string.isRequired,
};

export function UserOrderPage({ orderId }) {
    const api = useFrontAPI();
    const payments = usePaymentConfigs();
    const renderBlock = useRenderer();
    const { generateUrl } = useNavigationUtils();
    const { contacts } = useCRMData();
    const { theme } = usePageTheme();
    const { contentStyle } = useModalThemes();
    const [order, setOrder] = useState(null);
    const [orderLoadCount, setOrderLoadCount] = useState(0);
    const [isPayNowModal, togglePayNowModal] = useToggle(false);
    const [isShowingCancelPrompt, showCancelPrompt, hideCancelPrompt] = useBooleanHandler();

    const { loading: orderLoading, error, value: loadedOrder } = useAsync(
        () => api.orders.show({ orderId, query: { complete: true } }),
        [orderId, orderLoadCount]
    );
    const messengers = useMemo(
        () => (contacts.messengers.length ? [{ kind: MessengersBlockKind, messengers: contacts.messengers }] : []),
        [contacts.messengers]
    );
    const { immediate, online } = useMemo(() => {
        const mapper = ([provider]) => [provider, PAYMENT_PROVIDER_RENDERERS[provider]];

        return {
            immediate: payments.immediate.map(mapper),
            online: payments.online.map(mapper),
        };
    }, [payments]);
    const [{ loading: finalizeLoading }, handlePay] = useAsyncFn(
        async (paymentMethod) => {
            await api.orders.finalize({ orderId, payload: { paymentMethod } });
            togglePayNowModal();
            setOrderLoadCount((old) => old + 1);
        },
        [orderId]
    );
    const [{ loading: cancelLoading }, handleCancelOrder] = useAsyncFn(async () => {
        await api.orders.cancel({ orderId });
        hideCancelPrompt();
        setOrderLoadCount((old) => old + 1);
    }, [orderId]);

    useEffect(() => {
        if (loadedOrder) {
            setOrder(loadedOrder);
        }
    }, [loadedOrder]);

    if (!order || !order.finalized) {
        return null;
    }

    const loading = finalizeLoading || orderLoading || cancelLoading;
    const isJustOneOnlinePaymentMethod = !immediate.length && online.length === 1;
    const onlineElements = online.map(
        ([key, Renderer]) => order && <Renderer key={key} order={order} disabled={loading} onPay={handlePay} />
    );

    return (
        <ThemedModal
            overlay
            compact
            padding
            title={`${__t('order_term')} #${order.feedback.order}`}
            onClose={generateUrl('..')}
        >
            <div className={styles.root}>
                {error ? (
                    <div>{NotFoundError.is(error) ? __t('order_not_found') : error.message}</div>
                ) : (
                    <div>
                        <div className={styles.pane} style={theme.blocks.features}>
                            <div>
                                <div>{__t('order_status')}</div>
                                <div>{__t(`order_status_${order.feedback.status}`)}</div>
                            </div>
                            {handlesPaymentsStatus(order.paymentMethod) && (
                                <div>
                                    <div>{__t('payment_status')}</div>
                                    <div>{__t(`payment_status_${order.status}`)}</div>
                                </div>
                            )}
                        </div>
                        <h3 className={styles.sectionHeading}>{__t('your_contacts')}</h3>
                        <div className={styles.pane} style={theme.blocks.features}>
                            <div>
                                <div>{__t('name')}</div>
                                <div>
                                    {order.contactInfo.lastName} {order.contactInfo.firstName}
                                </div>
                            </div>
                            <div>
                                <div>{__t('phone')}</div>
                                <div>{order.contactInfo.phoneNumber}</div>
                            </div>
                            {order.shippingAddress && (
                                <div>
                                    <div>{__t('shipping')}</div>
                                    <div>
                                        {[
                                            order.shippingAddress.city,
                                            order.shippingAddress.state,
                                            order.shippingAddress.address,
                                            order.shippingAddress.postalCode,
                                        ]
                                            .filter(Boolean)
                                            .join(', ')}
                                    </div>
                                </div>
                            )}
                            {order.contactInfo.comment && (
                                <div>
                                    <div>{__t('comment')}</div>
                                    <div>{order.contactInfo.comment}</div>
                                </div>
                            )}
                        </div>
                        <div className={styles.sectionHeading}>{__t('order_content')}</div>
                        <div className={cn(styles.pane, styles.products)} style={theme.blocks.features}>
                            {order.items.map((product, index) => (
                                <div key={index}>
                                    <div>{product.name}</div>
                                    <div>
                                        {product.quantity}x{product.price.amount} {product.price.currency}
                                    </div>
                                </div>
                            ))}
                            <div>
                                <div>{__t('order_summary')}</div>
                                <div>
                                    {order.total.amount} {order.total.currency}
                                </div>
                            </div>
                        </div>

                        {messengers.length > 0 && (
                            <>
                                <h3 className={styles.sectionHeading}>{__t('store_contacts')}</h3>
                                <div className={cn(styles.pane, styles.messengers)} style={theme.blocks.features}>
                                    {messengers.map(renderBlock)}
                                </div>
                            </>
                        )}

                        <AnimatePresence>
                            {order.status === 'failed' && order.feedback.status !== 'rejected' && (
                                <motion.div
                                    key="order-actions"
                                    className={styles.buttons}
                                    exit={{ opacity: 0, height: 0, marginTop: '0px' }}
                                >
                                    {isJustOneOnlinePaymentMethod ? (
                                        onlineElements
                                    ) : (
                                        <ThemedButton theme={contentStyle} onClick={togglePayNowModal}>
                                            {__t('pay_order')}
                                        </ThemedButton>
                                    )}

                                    <ThemedButton theme={contentStyle} contour onClick={showCancelPrompt}>
                                        {__t('cancel_order')}
                                    </ThemedButton>
                                </motion.div>
                            )}

                            {!isJustOneOnlinePaymentMethod && isPayNowModal && (
                                <ThemedModal
                                    key="online-payment-options"
                                    overlay
                                    compact
                                    padding
                                    title={__t('choose_payment_method')}
                                    onClose={togglePayNowModal}
                                >
                                    <div className={styles.paymentButtons}>{onlineElements}</div>
                                </ThemedModal>
                            )}

                            {isShowingCancelPrompt && (
                                <ThemedModal
                                    key="order-cancellation"
                                    overlay
                                    compact
                                    padding
                                    title={__t('order_cancellation')}
                                    onClose={hideCancelPrompt}
                                >
                                    <div>
                                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                                            {__t('order_cancellation_prompt')}
                                        </div>

                                        <ThemedButton
                                            theme={contentStyle}
                                            onClick={handleCancelOrder}
                                            disabled={loading}
                                        >
                                            {__t('order_cancellation_prompt_button')}
                                        </ThemedButton>
                                    </div>
                                </ThemedModal>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </ThemedModal>
    );
}
